'use client'
import * as React from 'react'
import { ImgWithFallback } from '@/components/ui/img-with-fallback'
import { ArrowUpRight, Check, LayoutGrid } from 'lucide-react'
import { cn } from '@/lib/utils'
import { appSignupUrl, type WidgetView } from '@/lib/widget/embed'

// Browsable gallery of Idukki widget layout templates. Each card is a
// representative tile (a real Unsplash photo composited into a small CSS
// mock of the layout) so a visitor reads the shape of the layout at a
// glance, filters by use-case, then deep-links into the builder
// (/tools/widget-builder?view=…) or starts in the app. The layouts mirror
// the galleryView keys the widget renderer understands (see WidgetView).

type Category = 'fashion' | 'beauty' | 'reviews' | 'social'

type Template = {
  view: WidgetView
  name: string
  category: Category
  benefit: string
  /** Verified Unsplash photo IDs already used elsewhere in the repo. */
  imageIds: string[]
}

const CATEGORIES: { value: Category; label: string }[] = [
  { value: 'fashion', label: 'Fashion' },
  { value: 'beauty', label: 'Beauty' },
  { value: 'reviews', label: 'Reviews' },
  { value: 'social', label: 'Social' },
]

// All IDs verified present in the repo (components/marketing/use-case-strip,
// home + content surfaces) so no 404s.
const TEMPLATES: Template[] = [
  {
    view: 'grid',
    name: 'Shoppable grid',
    category: 'fashion',
    benefit: 'Tag every SKU in a clean grid so a scroll turns into a basket.',
    imageIds: ['photo-1490481651871-ab68de25d43d', 'photo-1485125639709-a60c3a500bf1', 'photo-1483985988355-763728e1935b', 'photo-1492725764893-90b379c2b6e7'],
  },
  {
    view: 'carousel',
    name: 'Hero carousel',
    category: 'fashion',
    benefit: 'A swipeable rail above the fold that keeps the page light and fast.',
    imageIds: ['photo-1492144534655-ae79c964c9d7', 'photo-1469334031218-e382a71b716b', 'photo-1490645935967-10de6ba17061'],
  },
  {
    view: 'masonry',
    name: 'Masonry wall',
    category: 'social',
    benefit: 'Mixed portrait and landscape UGC packed with no awkward gaps.',
    imageIds: ['photo-1469398715555-76331a6c7c9b', 'photo-1503236823255-94609f598e71', 'photo-1496747611176-843222e1e57c', 'photo-1469854523086-cc02fe5d8800'],
  },
  {
    view: 'collage',
    name: 'Editorial collage',
    category: 'fashion',
    benefit: 'One feature shot anchoring a tile cluster, lookbook energy.',
    imageIds: ['photo-1487412947147-5cebf100ffc2', 'photo-1483985988355-763728e1935b', 'photo-1492447166138-50c3889fccb1'],
  },
  {
    view: 'stories',
    name: 'Story rail',
    category: 'social',
    benefit: 'A sticky story bar of tappable circles, native to the mobile feed.',
    imageIds: ['photo-1488646953014-85cb44e25828', 'photo-1503454537195-1dcabb73ffb9', 'photo-1517336714731-489689fd1ca8', 'photo-1521737604893-d14cc237f11d'],
  },
  {
    view: 'reelCarousel',
    name: 'Reel carousel',
    category: 'social',
    benefit: 'Vertical video tiles that autoplay muted, built for short-form.',
    imageIds: ['photo-1518791841217-8f162f1e1131', 'photo-1556228720-195a672e8a03', 'photo-1490481651871-ab68de25d43d'],
  },
  {
    view: 'wall2',
    name: 'Social wall',
    category: 'social',
    benefit: 'A live, brand-safe feed for a landing page or an in-store screen.',
    imageIds: ['photo-1566073771259-6a8506099945', 'photo-1503376780353-7e6692767b70', 'photo-1495121605193-b116b5b9c5fe', 'photo-1493612276216-ee3925520721'],
  },
  {
    view: 'grid',
    name: 'Reviews grid',
    category: 'reviews',
    benefit: 'Star-rated photo reviews from Google, Trustpilot and Feefo in one block.',
    imageIds: ['photo-1487222477894-8943e31ef7b2', 'photo-1498837167922-ddd27525d352', 'photo-1503944583220-79d8926ad5e2', 'photo-1496181133206-80ce9b88a853'],
  },
  {
    view: 'masonry',
    name: 'Beauty masonry',
    category: 'beauty',
    benefit: 'Close-up results and swatches that read as proof before and after.',
    imageIds: ['photo-1457972729786-0411a3b2b626', 'photo-1515886657613-9f3515b0c78f', 'photo-1487412947147-5cebf100ffc2'],
  },
  {
    view: 'carousel',
    name: 'Beauty carousel',
    category: 'beauty',
    benefit: 'A tutorial-led rail that pairs the look with the product to buy.',
    imageIds: ['photo-1515562141207-7a88fb7ce338', 'photo-1457972729786-0411a3b2b626', 'photo-1492144534655-ae79c964c9d7'],
  },
  {
    view: 'collage',
    name: 'Lookbook collage',
    category: 'fashion',
    benefit: 'A magazine-style page block for a campaign or a seasonal drop.',
    imageIds: ['photo-1469334031218-e382a71b716b', 'photo-1492725764893-90b379c2b6e7', 'photo-1485125639709-a60c3a500bf1'],
  },
  {
    view: 'stories',
    name: 'Review stories',
    category: 'reviews',
    benefit: 'Tappable testimonial circles that surface five-star clips first.',
    imageIds: ['photo-1517423440428-a5a00ad493e8', 'photo-1498837167922-ddd27525d352', 'photo-1487222477894-8943e31ef7b2'],
  },
]

const unsplash = (id: string, w = 320, h = 320) =>
  `https://images.unsplash.com/${id}?auto=format&fit=crop&w=${w}&h=${h}&q=80`

export function TemplatesGallery() {
  const [active, setActive] = React.useState<Category | 'all'>('all')

  const filtered = active === 'all' ? TEMPLATES : TEMPLATES.filter((t) => t.category === active)

  return (
    <div>
      {/* Filter chips */}
      <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filter templates by use-case">
        <Chip active={active === 'all'} onClick={() => setActive('all')}>
          All templates
        </Chip>
        {CATEGORIES.map((c) => (
          <Chip key={c.value} active={active === c.value} onClick={() => setActive(c.value)}>
            {c.label}
          </Chip>
        ))}
        <span className="ml-auto hidden text-xs text-muted-foreground sm:inline" aria-live="polite">
          {filtered.length} layout{filtered.length === 1 ? '' : 's'}
        </span>
      </div>

      {/* Asymmetric gallery: first card spans two columns on wide screens. */}
      <ul className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.map((t, i) => (
          <li key={`${t.view}-${t.name}`} className={cn(i === 0 && 'sm:col-span-2 lg:col-span-2')}>
            <TemplateCard template={t} feature={i === 0} />
          </li>
        ))}
      </ul>

      {filtered.length === 0 && (
        <p className="mt-8 rounded-2xl border border-dashed bg-card p-10 text-center text-sm text-muted-foreground">
          No templates in this category yet. Pick another, every layout works for any vertical.
        </p>
      )}
    </div>
  )
}

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        active ? 'border-primary bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  )
}

function TemplateCard({ template, feature }: { template: Template; feature?: boolean }) {
  const builderHref = `/tools/widget-builder?view=${template.view}`
  return (
    <div className="group flex h-full flex-col overflow-hidden rounded-2xl border bg-card shadow-sm transition-[transform,box-shadow,border-color] duration-200 ease-out motion-safe:hover:-translate-y-1 hover:border-primary/40 hover:shadow-md focus-within:border-primary/40">
      <LayoutMock template={template} tall={feature} />
      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-center justify-between gap-3">
          <h3 className="font-display text-base font-semibold tracking-tight text-foreground">{template.name}</h3>
          <span className="shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            {template.category}
          </span>
        </div>
        <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">{template.benefit}</p>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <a
            href={builderHref}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground transition-colors duration-150 hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card"
          >
            Use this template
            <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
          </a>
          <a
            href={appSignupUrl({ ref: `tool-templates-${template.view}` })}
            className="inline-flex items-center rounded-lg border px-3 py-2 text-xs font-medium text-muted-foreground transition-colors duration-150 hover:border-primary/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Start in app
          </a>
        </div>
      </div>
    </div>
  )
}

// A small CSS rendering of each layout, filled with real photos, so the
// card communicates the shape of the layout rather than showing a generic
// icon. The photos zoom gently on hover (reduced-motion gated by Tailwind's
// motion-safe).
function LayoutMock({ template, tall }: { template: Template; tall?: boolean }) {
  const ids = template.imageIds
  const ratio = tall ? 'aspect-[16/7]' : 'aspect-[4/3]'
  const img = (id: string, alt: string, className?: string) => (
    <div className={cn('relative overflow-hidden bg-secondary', className)}>
      <ImgWithFallback
        src={unsplash(id)}
        alt={alt}
        fill
        sizes="(min-width: 1024px) 30vw, (min-width: 640px) 45vw, 90vw"
        className="object-cover transition-transform duration-500 ease-out motion-safe:group-hover:scale-[1.04]"
      />
    </div>
  )
  const alt = `${template.name} layout preview`

  let inner: React.ReactNode
  switch (template.view) {
    case 'grid':
      inner = (
        <div className="grid h-full grid-cols-2 grid-rows-2 gap-1.5">
          {ids.slice(0, 4).map((id, i) => img(id, alt, i === 0 && tall ? 'rounded-sm' : 'rounded-sm'))}
        </div>
      )
      break
    case 'carousel':
      inner = (
        <div className="flex h-full items-stretch gap-1.5">
          {img(ids[0], alt, 'flex-[2] rounded-sm')}
          {img(ids[1] ?? ids[0], alt, 'flex-1 rounded-sm')}
          {img(ids[2] ?? ids[0], alt, 'hidden flex-1 rounded-sm sm:block')}
        </div>
      )
      break
    case 'masonry':
      inner = (
        <div className="grid h-full grid-cols-3 grid-rows-2 gap-1.5">
          {img(ids[0], alt, 'row-span-2 rounded-sm')}
          {img(ids[1] ?? ids[0], alt, 'rounded-sm')}
          {img(ids[2] ?? ids[0], alt, 'row-span-2 rounded-sm')}
          {img(ids[3] ?? ids[1] ?? ids[0], alt, 'rounded-sm')}
        </div>
      )
      break
    case 'collage':
      inner = (
        <div className="grid h-full grid-cols-3 grid-rows-2 gap-1.5">
          {img(ids[0], alt, 'col-span-2 row-span-2 rounded-sm')}
          {img(ids[1] ?? ids[0], alt, 'rounded-sm')}
          {img(ids[2] ?? ids[0], alt, 'rounded-sm')}
        </div>
      )
      break
    case 'stories':
      inner = (
        <div className="flex h-full items-center gap-2.5 px-1">
          {ids.slice(0, tall ? 6 : 4).map((id, i) => (
            <div key={i} className="relative aspect-square w-1/4 shrink-0 overflow-hidden rounded-full ring-2 ring-primary/60">
              <ImgWithFallback src={unsplash(id, 160, 160)} alt={alt} fill sizes="80px" className="object-cover transition-transform duration-500 ease-out motion-safe:group-hover:scale-[1.06]" />
            </div>
          ))}
        </div>
      )
      break
    case 'reelCarousel':
      inner = (
        <div className="flex h-full items-stretch justify-center gap-2">
          {ids.slice(0, 3).map((id, i) => (
            <div key={i} className="relative aspect-[9/16] h-full overflow-hidden rounded-md bg-secondary">
              <ImgWithFallback src={unsplash(id, 180, 320)} alt={alt} fill sizes="120px" className="object-cover transition-transform duration-500 ease-out motion-safe:group-hover:scale-[1.04]" />
            </div>
          ))}
        </div>
      )
      break
    case 'wall2':
    default:
      inner = (
        <div className="grid h-full grid-cols-4 grid-rows-2 gap-1.5">
          {Array.from({ length: tall ? 8 : 8 }).map((_, i) => img(ids[i % ids.length], alt, 'rounded-sm'))}
        </div>
      )
      break
  }

  return (
    <div className={cn('relative w-full border-b bg-secondary/50 p-2.5', ratio)}>
      <div className="absolute inset-2.5">{inner}</div>
      <span className="absolute left-3 top-3 z-10 inline-flex items-center gap-1 rounded-md bg-card/95 px-1.5 py-0.5 text-[10px] font-semibold text-foreground shadow-sm backdrop-blur">
        <LayoutGrid className="h-3 w-3 text-primary" aria-hidden />
        {template.view}
      </span>
    </div>
  )
}

// Exported so the page can list the supported sources honestly without
// duplicating the strings.
export function TemplatesNote() {
  return (
    <p className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
      <Check className="h-3.5 w-3.5 text-primary" aria-hidden />
      Every layout ships with all sources and modules. No layout is gated behind a higher tier.
    </p>
  )
}
